import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, ImageUp, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import CompareDialog from "@/components/CompareDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg, img } from "@/lib/api";

const MAX = 100;

export function useUploader(bookId, onAccepted) {
  const [progress, setProgress] = useState(null);
  const [rejected, setRejected] = useState([]);
  const upload = useCallback(async (files, current) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    const room = MAX - current;
    if (list.length > room) toast.warning(`A book holds up to ${MAX} photos — only the first ${room} will be added.`);
    const batch = list.slice(0, Math.max(room, 0));
    const bad = [];
    let fixed = 0;
    for (let i = 0; i < batch.length; i += 4) {
      setProgress(Math.round((i / batch.length) * 100));
      const fd = new FormData();
      batch.slice(i, i + 4).forEach((f) => fd.append("files", f));
      try {
        const { data } = await api.post(`/books/${bookId}/photos`, fd);
        onAccepted(data.accepted);
        fixed += data.accepted.filter((p) => p.fixes?.length).length;
        bad.push(...data.rejected);
      } catch (e) { toast.error(errMsg(e)); }
    }
    setProgress(null);
    if (fixed) toast.success(fixed === 1 ? "1 photo was a little soft or small — we've enhanced it for print." : `${fixed} photos were a little soft or small — we've enhanced them for print.`, { duration: 6000 });
    if (bad.length) setRejected(bad);
  }, [bookId, onAccepted]);
  return { upload, progress, rejected, setRejected };
}

export function LowResDialog({ rejected, onClose, onPickAnother }) {
  const low = rejected.filter((r) => r.reason === "low_res");
  const other = rejected.filter((r) => r.reason !== "low_res");
  return (
    <AlertDialog open={rejected.length > 0} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="dpi-warning-alert-dialog" className="bg-[#FDFBF7]">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 font-serif text-2xl">
            <AlertTriangle className="h-5 w-5 text-[#D97706]" />
            {low.length ? `${low.length === 1 ? "This photo is" : `${low.length} photos are`} too tiny to rescue` : "Some photos couldn't be added"}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-[#5E5868]">
              {low.length > 0 && <p>We automatically enhance small or soft photos for print, but these are icon-sized thumbnails with too little detail to work with. The original from your camera or WhatsApp "HD" usually works.</p>}
              <ul className="max-h-48 space-y-1.5 overflow-auto" data-testid="dpi-rejected-list">
                {rejected.map((r, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2">
                    <span className="truncate">{r.filename}</span>
                    <span className="shrink-0 font-mono text-xs text-[#D97706]">{r.reason === "low_res" ? `${r.width}×${r.height}px` : r.message}</span>
                  </li>
                ))}
              </ul>
              {other.length > 0 && low.length > 0 && <p className="text-xs">Some files were skipped for other reasons (shown above).</p>}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="dpi-warning-skip-btn">Skip these</AlertDialogCancel>
          {low.length > 0 && <AlertDialogAction data-testid="dpi-warning-swap-photo-btn" onClick={onPickAnother} className="bg-[#3B5BDB] hover:bg-[#2F49B5]">Choose another photo</AlertDialogAction>}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function UploadStep({ book, onDone }) {
  const [photos, setPhotos] = useState([]);
  const [drag, setDrag] = useState(false);
  const [compare, setCompare] = useState(null);
  const input = useRef();
  useEffect(() => { api.get(`/books/${book.id}/photos`).then((r) => setPhotos(r.data)); }, [book.id]);
  const onAccepted = useCallback((acc) => setPhotos((p) => [...p, ...acc]), []);
  const { upload, progress, rejected, setRejected } = useUploader(book.id, onAccepted);
  const remove = async (id) => { await api.delete(`/photos/${id}`); setPhotos((p) => p.filter((x) => x.id !== id)); };

  return (
    <div className="rise">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl tracking-tight sm:text-5xl">Add your photos</h1>
          <p className="mt-2 text-[#5E5868]">Up to {MAX} photos. Add more than you need — we'll pick the best.</p>
        </div>
        <p className="font-mono text-sm text-[#8C8498]" data-testid="upload-count">{photos.length} / {MAX}</p>
      </div>
      <div
        data-testid="wizard-upload-dropzone"
        onClick={() => input.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files, photos.length); }}
        className={`mt-8 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-14 text-center transition-colors ${drag ? "border-[#3B5BDB] bg-[#3B5BDB]/5" : "border-[#E8E2D9] bg-white/60 hover:border-[#d9cfb6]"}`}
      >
        <ImageUp className="h-9 w-9 text-[#3B5BDB]" strokeWidth={1.3} />
        <p className="mt-4 font-medium">Drag photos here, or tap to choose</p>
        <p className="mt-1 text-xs text-[#8C8498]">JPG, PNG or WEBP · small or slightly blurry photos are enhanced automatically for print</p>
        <input ref={input} type="file" multiple accept="image/jpeg,image/png,image/webp" hidden data-testid="upload-file-input"
          onChange={(e) => { upload(e.target.files, photos.length); e.target.value = ""; }} />
      </div>
      {progress !== null && <div className="mt-4" data-testid="upload-progress"><Progress value={progress} className="h-1.5" /><p className="mt-2 text-xs text-[#8C8498]">Uploading & checking quality…</p></div>}
      {photos.length > 0 && (
        <div className="mt-8 grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-10" data-testid="upload-grid">
          {photos.map((p) => (
            <div key={p.id} className="group relative aspect-square overflow-hidden rounded-sm bg-[#F5F0E8]">
              <img src={img(p.url)} alt={p.filename} className="h-full w-full object-cover" />
              {p.fixes?.length > 0 && <button type="button" onClick={() => setCompare(p)} data-testid={`upload-fixed-${p.id}`} title="Tap to compare before / after" className="absolute bottom-1 left-1 flex items-center gap-0.5 rounded bg-[#3B5BDB]/90 px-1 py-px text-[9px] font-medium text-white hover:bg-[#3B5BDB]"><Sparkles className="h-2.5 w-2.5" />Enhanced</button>}
              <button data-testid={`upload-remove-${p.id}`} onClick={() => remove(p.id)} className="absolute right-1 top-1 rounded-full bg-black/50 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100 max-sm:opacity-100" aria-label="Remove">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="sticky bottom-0 mt-10 flex items-center justify-between gap-4 border-t border-[#E8E2D9] bg-[#FDFBF7]/90 py-4 backdrop-blur">
        <p className="text-sm text-[#5E5868]">{photos.length < 4 ? "Add at least 4 photos to continue" : "Looking good. Ready when you are."}</p>
        <button data-testid="wizard-generate-btn" disabled={photos.length < 4 || progress !== null} onClick={onDone} className="btn-primary">Make my book</button>
      </div>
      <LowResDialog rejected={rejected} onClose={() => setRejected([])} onPickAnother={() => { setRejected([]); input.current.click(); }} />
      <CompareDialog photo={compare} onClose={() => setCompare(null)} />
    </div>
  );
}
